import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SpotlightBottomBarComponent } from '../../dashboard/ui-editor/spotlight/shell/bottom-bar.component';
// Was (2026-10-06, nav 8b final review, M1 — the template shows <app-shared-history> now, so this import was dead, NG8113):
// import { StudioHistoryComponent } from '../../dashboard/ui-editor/spotlight/shell/studio-history.component';
import type { CrumbPill } from '../../dashboard/ui-editor/spotlight/shell/shell.types';
import type { HistoryEntry } from '../../dashboard/ui-editor/spotlight/shell/history.util';
import { ClientService } from '../../services/client.service';
import { GlobalVariablesService } from '../../services/global-variables.service';
import { StudioNavService } from '../studio-nav.service';
// Nav 8b Task 3 — the panel shows the tab's one shared history, not this bar's own messages.
import { SharedHistoryComponent } from '../shared-history.component';
import { isLightScreen } from '../screen-tone';

/** Screens where no studio chat may show (sign-in, refusal, runtime landing). */
const AUTH_ROUTES = /^\/(\?|$)|^\/(resetPassword|team-only|accept-invite|landing|blank)/;
/**
 * Nav 7b final review (I6) — screens whose own controls sit where the bar would: AI Query is a chat of its own, the
 * page builder has Spotlight (a lane) or its preview buttons, and the function editor has its bottom-right tools and Forge.
 */
const OWN_CONTROLS_ROUTES = /^\/(query|ui-editor|dashboard\/function-editor)/;

/**
 * THE STUDIO BAR (nav 7b; spec Q1, Q2, R2). One bar on every editor screen that has no chat of its own: it navigates
 * (the navigator session) and answers questions about the screen. It hides itself on sign-in screens and where another
 * lane owns the bar (the Data page's entity chat, Spotlight) — those lanes route navigation through the same session.
 */
@Component({
  selector: 'app-studio-bar',
  standalone: true,
  // Was (2026-10-06, nav 8b Task 3): imports: [CommonModule, SpotlightBottomBarComponent, StudioHistoryComponent],
  // Was (2026-10-06, nav 8b final review, M1): imports: [CommonModule, SpotlightBottomBarComponent, StudioHistoryComponent, SharedHistoryComponent],
  imports: [CommonModule, SpotlightBottomBarComponent, SharedHistoryComponent],
  templateUrl: './studio-bar.component.html',
})
export class StudioBarComponent {
  readonly nav = inject(StudioNavService);
  private router = inject(Router);
  private clients = inject(ClientService);
  private globals = inject(GlobalVariablesService);
  // Was (2026-10-06, nav 8b Task 3 — the text now lives in the shared draft, so it survives a change of screen): input = '';
  get input(): string { return this.nav.draft(); }
  set input(v: string) { this.nav.draft.set(v); }
  panelOpen = false;
  private url = this.router.url;

  constructor() {
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((e) => {
      this.url = (e as NavigationEnd).urlAfterRedirects;
      // Nav 7b (T4 review): a navigator-made move runs while the session is busy, so its own "Opened …"
      // reply is pushed after this and stays; a hand-made move clears the card.
      if (!this.session.busy) this.session.dismiss();
    });
  }

  // Was (2026-10-05, nav 7b final review, I6 — the bar covered AI Query's chat, the page builder and the function editor):
  // get visible(): boolean { return this.nav.lane() === 'nav' && !AUTH_ROUTES.test(this.url); }
  get visible(): boolean { return this.nav.lane() === 'nav' && !AUTH_ROUTES.test(this.url) && !OWN_CONTROLS_ROUTES.test(this.url); }
  get session() { return this.nav.session; }
  /**
   * The bar matches the screen it is on: white on Bastion, white on a switchable screen switched to white, dark everywhere
   * else. A getter, because the switch is a plain field nothing can watch — every check reads it again, so flipping the
   * sun/moon or moving screen changes the bar at once.
   */
  get light(): boolean { return isLightScreen(this.url, this.globals.pageThemeDark); }
  get crumbs(): CrumbPill[] {
    return [{ label: this.clients.selectedClient?.name || 'Zavo', jsonPath: '', kind: 'page' } as CrumbPill];
  }
  // Nav 8b Task 3 — the template no longer reads this; the panel shows <app-shared-history> instead. Left in
  // place in case another caller still wants this chat's own entries (none does, as of this task).
  get historyEntries(): HistoryEntry[] {
    return this.session.messages.map((m) => ({ role: m.role, text: m.text, group: m.requestId, notice: m.notice }));
  }
  get lastReply(): string | null {
    // Was (2026-10-05, nav 7b T4 review): const last = [...this.session.messages].reverse().find((m) => m.role === 'assistant');
    const last = this.session.messages.slice(this.session.shownFrom).reverse().find((m) => m.role === 'assistant');
    return last?.text ?? null;
  }

  async send(text: string): Promise<void> {
    this.input = '';
    await this.session.send(text);
  }
  async choose(id: string): Promise<void> { await this.session.choose(id); }
  close(): void { this.session.dismiss(); }
  byIndex(index: number): number { return index; }
}
